package com.evoapps.evoshift.photo

import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.toComposeImageBitmap
import kotlinx.browser.document
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.skia.Image
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.khronos.webgl.Uint8ClampedArray
import org.khronos.webgl.set
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

/** Skia (what Compose draws with on the web): JPEG, PNG, GIF, WebP. */
internal actual fun decodeImage(bytes: ByteArray, maxSide: Int): ImageBitmap? =
    runCatching { Image.makeFromEncoded(bytes).toComposeImageBitmap() }.getOrNull()

/**
 * The browser's own JPEG encoder: the pixels are put on a canvas and `toBlob("image/jpeg")`
 * encodes them (a canvas has no metadata to carry over).
 */
internal actual suspend fun encodeJpeg(image: ImageBitmap, quality: Int): ByteArray {
    val width = image.width
    val height = image.height
    val pixels = IntArray(width * height)
    image.readPixels(pixels)
    val canvas = document.createElement("canvas") as HTMLCanvasElement
    canvas.width = width
    canvas.height = height
    val context = canvas.getContext("2d") as CanvasRenderingContext2D
    val data = context.createImageData(width.toDouble(), height.toDouble())
    val rgba: Uint8ClampedArray = data.data
    for (i in pixels.indices) {
        val argb = pixels[i]
        rgba[i * 4] = ((argb shr 16) and 0xFF).toByte()
        rgba[i * 4 + 1] = ((argb shr 8) and 0xFF).toByte()
        rgba[i * 4 + 2] = (argb and 0xFF).toByte()
        rgba[i * 4 + 3] = 0xFF.toByte()
    }
    context.putImageData(data, 0.0, 0.0)
    return suspendCancellableCoroutine { continuation ->
        canvas.toBlob({ blob ->
            if (blob == null) {
                continuation.resumeWithException(IllegalStateException("JPEG encoding failed"))
            } else {
                val reader = FileReader()
                reader.onload = {
                    val buffer = reader.result.unsafeCast<ArrayBuffer>()
                    continuation.resume(Int8Array(buffer).unsafeCast<ByteArray>())
                }
                reader.onerror = { continuation.resumeWithException(IllegalStateException("JPEG encoding failed")) }
                reader.readAsArrayBuffer(blob)
            }
        }, "image/jpeg", quality / 100.0)
    }
}
